import React, { createContext, useState, useEffect } from 'react';

export type ConfigContextType = {
    cognito?: CognitoConfig;
};

export type CognitoConfig = {
    userPoolId: string;
    clientId: string;
    region?: string;
};

const cognito_env_config: CognitoConfig = {
    userPoolId: import.meta.env.VITE_COGNITO_USER_POOL_ID || 'us-east-1_xuNNxt1CG',
    clientId: import.meta.env.VITE_COGNITO_CLIENT_ID || '1n3g2fsletc7cho504lhp6r07k',
    region: import.meta.env.VITE_COGNITO_REGION || 'us-east-1',
};


const configContext = createContext<ConfigContextType>({
    cognito: cognito_env_config
});

const ConfigContextProvider: React.FC<{
    children: React.ReactNode;
    loading?: React.ReactNode;
}> = ({ children, loading }) => {

    const [cognitoConfig, setCognitoConfig] = useState<CognitoConfig | undefined>(undefined);
    const [isLoading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);


    const fetchConfig = async (url: string): Promise<any | null> => {
        setLoading(true);
        setError(null);  
        try {
            const response = await fetch(url);
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            const result = await response.json();
            return result;
        } catch (error) {
            console.error('Error fetching config:', error);
            return null;
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        // Try to fetch config from the API endpoint first
        fetchConfig('/api/config')
            .then((config) => {
                if (config && config.cognito) {
                    // API response directly matches our CognitoConfig type
                    const normalizedConfig: CognitoConfig = {
                        userPoolId: config.cognito.userPoolId,
                        clientId: config.cognito.clientId,
                        region: 'us-east-1' // Default region
                    };
                    console.log('Using cognito config from API endpoint:');
                    console.log('  UserPoolId:', normalizedConfig.userPoolId);
                    console.log('  ClientId:', normalizedConfig.clientId);
                    setCognitoConfig(normalizedConfig);
                } else {
                    throw new Error('Invalid API response');
                }
            })
            .catch((apiError) => {
                console.warn('API config endpoint failed, falling back to env config:', apiError);
                
                // Fallback to environment config
                console.log('Using cognito config from environment variables:');
                console.log('  UserPoolId:', cognito_env_config.userPoolId);
                console.log('  ClientId:', cognito_env_config.clientId);
                setCognitoConfig(cognito_env_config);
            });
    }, []);

    if (isLoading) {
        return loading || <div>Loading...</div>;
    }

    if (error) {
        return <div>Error: {error}</div>;
    }

    return (
        <configContext.Provider value={{
            cognito: cognitoConfig
        }}>
            {children}
        </configContext.Provider>
    );
};

export default ConfigContextProvider;
export { configContext, ConfigContextProvider };